import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import SupportBell from "@/components/SupportBell";
import AiPointsBadge from "@/components/AiPointsBadge";
import { isAdminUser, canAccessCrm } from "@/lib/api";
import { Home, Users, Ticket, DollarSign, User as UserIcon, LogOut, BookOpenCheck, ShoppingBag, Lock, ChevronDown, ChefHat, CalendarDays, Boxes, UserRound } from "lucide-react";
import { useHouseBrand } from "@/hooks/useHouseBrand";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";

// CRM, Coupons and Events require an approved account (Airtable Users.approved
// = 1). Admins always have access. Non-approved users see a locked tab that
// explains they need approval.
function authedItems(user) {
  const admin = isAdminUser(user);
  const access = canAccessCrm(user);
  const crmLabel = "CRM";
  // Home tab is removed for everyone — clicking the logo navigates to Orders.
  const items = [
    { to: "/app/contacts", label: crmLabel, icon: Users, testId: "topnav-contacts", gated: !access },
    { to: "/app/coupons", label: "Coupons", icon: Ticket, testId: "topnav-coupons", gated: !access },
  ];
  if (admin) {
    items.push({
      to: "/app/catalogue",
      label: "Catalogue",
      icon: BookOpenCheck,
      testId: "topnav-catalogue",
    });
  }
  items.push({
    to: "/app/earnings",
    label: "Orders",
    icon: DollarSign,
    testId: "topnav-earnings",
  });
  items.push({
    to: "/app/folio",
    label: "Folio",
    icon: Boxes,
    testId: "topnav-folio",
    gated: !access,
  });
  items.push({
    to: "/app/services",
    label: "Events",
    icon: CalendarDays,
    testId: "topnav-events",
    gated: !access,
  });
  return items;
}

export default function TopNav({ variant = "app" }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const brand = useHouseBrand();

  return (
    <header
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-zinc-200"
      data-testid="top-nav"
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-3">
        <Link
          to="/d/orevital?view=shop"
          className="flex items-center shrink-0"
          data-testid="topnav-brand"
        >
          <BrandLogo alt={brand.company} />
        </Link>

        {variant === "auth" && (
          <nav
            className="flex items-center gap-1 sm:gap-2 overflow-x-auto min-w-0 flex-1 sm:justify-end"
            data-testid="topnav-auth-row"
          >
            <Link
              to="/d/orevital?view=home"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full text-zinc-700 hover:bg-zinc-100 transition-colors"
              data-testid="topnav-auth-home"
            >
              <Home size={14} />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <Link
              to="/d/orevital"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              data-testid="topnav-auth-brands"
            >
              <ShoppingBag size={14} />
              Shop
            </Link>
            <Link
              to="/blog"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full text-zinc-700 hover:bg-zinc-100 transition-colors"
              data-testid="topnav-auth-blog"
            >
              <ChefHat size={14} />
              <span className="hidden sm:inline">Blog</span>
            </Link>
            <Link
              to="/events"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full text-emerald-700 hover:bg-emerald-50 transition-colors"
              data-testid="topnav-auth-events"
            >
              <CalendarDays size={14} />
              <span className="hidden sm:inline">Events</span>
            </Link>
            <SupportBell />
          </nav>
        )}
        {variant === "auth" && (
          <Link
            to="/login"
            className="shrink-0 h-10 w-10 rounded-full bg-violet-50 hover:bg-violet-100 text-violet-700 flex items-center justify-center"
            data-testid="topnav-signin"
            aria-label="Sign in"
            title="Sign in"
          >
            <UserRound size={16} />
          </Link>
        )}

        {variant === "app" && user && (
          <nav
            className="flex items-center gap-1"
            data-testid="topnav-app-row"
          >
            <div className="hidden sm:flex items-center gap-1">
              {authedItems(user).map((it) => (
                <NavItem key={it.to} item={it} compact />
              ))}
            </div>
            <AiPointsBadge user={user} />
            <SupportBell />
            <div className="ml-1 shrink-0">
              <ProfileMenu
                user={user}
                onLogout={() => {
                  logout();
                  nav("/login", { replace: true });
                }}
              />
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}

function NavItem({ item, compact = false }) {
  const { to, label, icon: Icon, testId, gated } = item;
  if (gated) {
    return (
      <button
        type="button"
        onClick={() =>
          toast.message("Approval required", {
            description: `${label} is available to approved dealers. Reach out to your sponsor to get your account approved.`,
          })
        }
        data-testid={testId}
        className={`flex items-center gap-1.5 ${compact ? "px-2 py-1" : "px-3 py-1.5"} rounded-full text-xs font-semibold text-zinc-400 hover:bg-zinc-50 transition-colors`}
        title="Approval required"
      >
        <Lock size={11} />
        <span>{label}</span>
      </button>
    );
  }
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-1.5 ${compact ? "px-2 py-1" : "px-3 py-1.5"} rounded-full text-xs font-semibold transition-colors ${
          isActive
            ? "bg-violet-50 text-violet-700"
            : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
        }`
      }
    >
      <Icon size={compact ? 13 : 14} />
      <span>{label}</span>
    </NavLink>
  );
}

function ProfileMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold transition-colors ${
          open ? "bg-violet-50 text-violet-700" : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
        }`}
        aria-label="Profile menu"
        aria-expanded={open}
        data-testid="topnav-profile"
      >
        <UserIcon size={13} />
        <span>Admin</span>
        <ChevronDown size={12} className="opacity-60" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-52 rounded-xl bg-white shadow-xl border border-zinc-200 overflow-hidden z-50"
          data-testid="topnav-account-menu"
        >
          <Link
            to="/app/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-zinc-800 hover:bg-zinc-50"
            data-testid="topnav-menu-profile"
          >
            <UserIcon size={15} className="text-zinc-500" />
            Manage
          </Link>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-rose-600 hover:bg-rose-50 border-t border-zinc-100"
            data-testid="topnav-menu-signout"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
